import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { user, register, registrationOpen, refreshMe, formatApiError } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    refreshMe().finally(() => setChecking(false));
  }, [refreshMe]);

  if (user && user !== false) return <Navigate to="/" replace />;
  if (checking) {
    return <div className="p-10 text-slate-500 text-sm">Checking registration status…</div>;
  }
  if (!registrationOpen) return <Navigate to="/login" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (password.length < 8) {
      toast.error("Password too short", { description: "Use 8+ characters." });
      return;
    }
    setBusy(true);
    try {
      await register(email.trim(), password, name.trim());
      toast.success("Welcome — admin account created");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error("Registration failed", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-10">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm bg-white rounded-lg border border-slate-200 p-6 sm:p-8"
        data-testid="register-form"
      >
        <div className="flex items-center gap-2 mb-6">
          <div
            className="h-9 w-9 rounded-md flex items-center justify-center text-white font-bold"
            style={{ background: "hsl(var(--brand))" }}
          >
            RC
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900 font-display">First-run setup</div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Create admin</div>
          </div>
        </div>

        <h2 className="text-2xl font-semibold tracking-tight text-slate-900 font-display">
          Create the first admin
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          This option closes once any user exists.
        </p>

        <div className="space-y-4 mt-6">
          <div>
            <Label htmlFor="name" className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Name
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 min-h-[44px]"
              data-testid="register-name-input"
            />
          </div>
          <div>
            <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 min-h-[44px]"
              data-testid="register-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Password
            </Label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 min-h-[44px]"
              data-testid="register-password-input"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={busy}
          className="w-full mt-6 min-h-[44px] text-white"
          style={{ background: "hsl(var(--brand))" }}
          data-testid="register-submit-button"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Create admin
        </Button>
      </form>
    </div>
  );
}
